import React from 'react';
import {
  Button, Modal, Spinner,
} from 'react-bootstrap';
import type { HubTagTemplateUsedInStory } from 'dorian-shared/types/branch/HubTagTemplate';
import { ConfirmModal } from '../../ui/ConfirmModal/ConfirmModal';
import { TagTemplateRow } from './TagTemplateRow';
import { useTagTemplatesModal } from './useTagTemplatesModal';
import css from './HubTagTemplatesModal.module.scss';

const EMPTY_USED_IN_STORY: HubTagTemplateUsedInStory[] = [];

type HubTagTemplatesModalProps = {
  onHide: () => void;
  readOnly?: boolean;
};

export function HubTagTemplatesModal({ onHide, readOnly }: HubTagTemplatesModalProps) {
  const {
    localTemplates,
    templateErrors,
    isLoading,
    isSaving,
    isDirty,
    isDisabled,
    hasAnyError,
    deleteConfirmIndex,
    deleteTemplateName,
    handleChange,
    handleAdd,
    handleDeleteRequest,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleSave,
  } = useTagTemplatesModal(onHide);

  return (
    <Modal
      show
      onHide={onHide}
      size="xl"
      backdrop="static"
      keyboard={false}
      centered
      scrollable
      contentClassName={css.contentClassName}
    >
      <Modal.Header closeButton>
        <Modal.Title>Tag Templates</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted mb-3">
          Tag templates define how a tag <strong>looks</strong> (text, color, icon).
          When and where a tag is <strong>shown</strong> is configured on each hub option.
        </p>

        {isLoading && (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        {!isLoading && localTemplates.map((template, index) => (
          <TagTemplateRow
            key={template.localKey}
            template={template}
            nameError={templateErrors[index].nameError}
            contentError={templateErrors[index].contentError}
            onChange={(updated) => handleChange(index, updated)}
            onDelete={() => handleDeleteRequest(index)}
            usedInStory={template.usedInStory ?? EMPTY_USED_IN_STORY}
            disabled={readOnly}
          />
        ))}

        <div className="d-flex align-items-center gap-3 mt-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleAdd}
            disabled={isDisabled || readOnly}
            data-testid="add-tag-template-btn"
          >
            + Add Tag Template
          </Button>
          <span className="text-muted small">
            {localTemplates.length}
            {' '}
            template
            {localTemplates.length !== 1 ? 's' : ''}
            {' '}
            defined
          </span>
        </div>

        <ConfirmModal
          centered
          show={deleteConfirmIndex != null}
          isLoading={isDisabled}
          title="Delete Tag Template"
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          className="overlay"
          backdrop="static"
        >
          <div className="text-center">
            Are you sure you want to delete this tag template?
            <br />
            <b>{deleteTemplateName}</b>
          </div>
        </ConfirmModal>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isDisabled || !isDirty || hasAnyError || readOnly}
          data-testid="save-tag-templates-btn"
        >
          {isSaving ? <Spinner animation="border" size="sm" /> : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
